import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpIcon from '@mui/icons-material/Help';
import DatasetIcon from '@mui/icons-material/Dataset';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import NatureIcon from '@mui/icons-material/Nature';
import RouteIcon from '@mui/icons-material/Route';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChatIcon from '@mui/icons-material/Chat';
import Collapse from '@mui/material/Collapse';
import { useNavigate, useLocation } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';

// Pages
import FileUploadPage from './pages/FileUploadPage';
import ProcessingPage from './pages/ProcessingPage';
import DataAnalysisPage from './pages/DataAnalysisPage';
import ChatPage from './pages/ChatPage';
// New Dashboard Pages
import DataPrepPage from './pages/dashboard/DataPrepPage';
import SpendCubeDimensionsPage from './pages/dashboard/SpendCubeDimensionsPage';
import SupplierConsolidationPage from './pages/dashboard/SupplierConsolidationPage';
import PriceVariancesPage from './pages/dashboard/PriceVariancesPage';
import TailSpendPage from './pages/dashboard/TailSpendPage';
import ProcessEfficiencyPage from './pages/dashboard/ProcessEfficiencyPage';
import SustainabilityPage from './pages/dashboard/SustainabilityPage';
import OpportunityRoadmapPage from './pages/dashboard/OpportunityRoadmapPage';

// Context
import { FileDataProvider } from './context/FileDataContext';

// Add new interfaces for project data
interface ProjectStakeholder {
  id: string;
  name: string;
  role: string;
  email: string;
}

interface ProjectData {
  id: string;
  name: string;
  description: string;
  startDate: string | null;
  endDate: string | null;
  department: string;
  type: string;
  budget: number;
  currency: string;
  tags: string[];
  stakeholders: ProjectStakeholder[];
  createdAt: Date;
  updatedAt: Date;
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#8e2c4c', // Deep burgundy from the image
      light: '#a93e5e',
      dark: '#6e1c3c',
    },
    secondary: {
      main: '#e84118', // Orange accent color from the login button
      light: '#f0603a',
      dark: '#c23616',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#2d3436',
      secondary: '#636e72',
    },
  },
  typography: {
    fontFamily: [
      'Roboto',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundColor: '#ffffff',
          color: '#2d3436',
          borderBottom: '1px solid #e0e0e0',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: 'none',
        },
      },
    },
  },
});

const drawerMinWidth = 280;

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [pageTitle, setPageTitle] = useState('Upload Data');
  const [drawerWidth, setDrawerWidth] = useState(drawerMinWidth);
  const [spendAnalysisOpen, setSpendAnalysisOpen] = useState(false);
  
  // Project management state - initialize from localStorage if available
  const [projects, setProjects] = useState<string[]>(() => {
    const savedProjects = localStorage.getItem('spendAnalyticsProjects');
    return savedProjects ? JSON.parse(savedProjects) : ['March 2025'];
  });
  
  const [currentProject, setCurrentProject] = useState<string>(() => {
    const savedCurrentProject = localStorage.getItem('spendAnalyticsCurrentProject');
    return savedCurrentProject || 'March 2025';
  });
  
  const [newProjectDialogOpen, setNewProjectDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState<Partial<ProjectData>>({
    name: '',
    description: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: null,
    department: '',
    type: 'Spend Analysis',
    budget: 0,
    currency: 'USD',
    tags: [],
    stakeholders: []
  });
  const [projectMenuAnchorEl, setProjectMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteProjectDialogOpen, setDeleteProjectDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);
  const [projectActionMenuAnchorEl, setProjectActionMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProjectForAction, setSelectedProjectForAction] = useState<string | null>(null);
  
  // Save projects to localStorage when they change
  useEffect(() => {
    localStorage.setItem('spendAnalyticsProjects', JSON.stringify(projects));
  }, [projects]);
  
  // Save current project to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('spendAnalyticsCurrentProject', currentProject);
  }, [currentProject]);
  
  const titleRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateDrawerWidth = () => {
      if (titleRef.current) {
        const titleWidth = titleRef.current.offsetWidth + 100; 
        setDrawerWidth(Math.max(drawerMinWidth, titleWidth));
      }
    };
    
    updateDrawerWidth();
    
    window.addEventListener('resize', updateDrawerWidth);
    
    return () => {
      window.removeEventListener('resize', updateDrawerWidth);
    };
  }, []);

  // Check if current location is any of the spend analysis subpages
  useEffect(() => {
    const isSpendAnalysisPage = location.pathname.startsWith('/dashboard/') || location.pathname === '/analysis';
    if (isSpendAnalysisPage && !spendAnalysisOpen) {
      setSpendAnalysisOpen(true);
    }
  }, [location.pathname, spendAnalysisOpen]);

  // Main menu items
  const mainMenuItems = [
    { text: 'Upload Data', icon: <UploadFileIcon />, path: '/upload' },
    { text: 'Chat', icon: <ChatIcon />, path: '/chat' }
  ];

  // Spend Analysis submenu items
  const spendAnalysisSubMenuItems = [
    { text: 'Overview', icon: <BarChartIcon />, path: '/analysis' },
    { text: 'Data Prep & Cleansing', icon: <CleaningServicesIcon />, path: '/dashboard/data-prep' },
    { text: 'Spend Cube Dimensions', icon: <ViewInArIcon />, path: '/dashboard/spend-cube' },
    { text: 'Supplier Consolidation', icon: <BusinessCenterIcon />, path: '/dashboard/supplier-consolidation' },
    { text: 'Price Variances', icon: <PriceCheckIcon />, path: '/dashboard/price-variances' },
    { text: 'Tail Spend Analysis', icon: <ReceiptLongIcon />, path: '/dashboard/tail-spend' },
    { text: 'Process Efficiency', icon: <DatasetIcon />, path: '/dashboard/process-efficiency' },
    { text: 'Sustainability & ESG', icon: <NatureIcon />, path: '/dashboard/sustainability' },
    { text: 'Opportunity Roadmap', icon: <RouteIcon />, path: '/dashboard/opportunity-roadmap' },
  ];

  // Handle spend analysis menu toggle
  const handleSpendAnalysisClick = () => {
    setSpendAnalysisOpen(!spendAnalysisOpen);
  };

  // Update page title based on current path
  useEffect(() => {
    // For main menu items
    const mainMenuItem = mainMenuItems.find(item => item.path === location.pathname);
    if (mainMenuItem) {
      setPageTitle(mainMenuItem.text);
      return;
    }

    // For spend analysis submenu items
    const subMenuItem = spendAnalysisSubMenuItems.find(item => item.path === location.pathname);
    if (subMenuItem) {
      setPageTitle(subMenuItem.text);
      return;
    }

    // For other paths
    if (location.pathname === '/processing') {
      setPageTitle('Processing');
    }
  }, [location.pathname]);

  // Handle project dialog open/close
  const handleOpenNewProjectDialog = () => {
    setNewProjectDialogOpen(true);
  };

  const handleCloseNewProjectDialog = () => {
    setNewProjectDialogOpen(false);
    // Reset the form
    setNewProject({
      name: '',
      description: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: null,
      department: '',
      type: 'Spend Analysis',
      budget: 0,
      currency: 'USD',
      tags: [],
      stakeholders: []
    });
  };

  // Handle creating a new project
  const handleCreateNewProject = () => {
    if (newProject.name && newProject.name.trim() !== '' && !projects.includes(newProject.name)) {
      // Add the new project to projects array
      setProjects([...projects, newProject.name]);
      setCurrentProject(newProject.name);
      
      // In a real application, you would save all project data to database/state
      // For now, we're just using the project name for simplicity
      console.log('New project created:', newProject);
      
      handleCloseNewProjectDialog();
    } else if (projects.includes(newProject.name || '')) {
      // Handle duplicate project name
      alert('A project with this name already exists');
    }
  };

  // Handle project menu
  const handleOpenProjectMenu = (event: React.MouseEvent<HTMLElement>) => {
    setProjectMenuAnchorEl(event.currentTarget);
  };

  const handleCloseProjectMenu = () => {
    setProjectMenuAnchorEl(null);
  };

  const handleSelectProject = (project: string) => {
    setCurrentProject(project);
    handleCloseProjectMenu();
  };

  // Handle project action menu
  const handleOpenProjectActionMenu = (event: React.MouseEvent<HTMLElement>, project: string) => {
    event.stopPropagation();
    setSelectedProjectForAction(project);
    setProjectActionMenuAnchorEl(event.currentTarget);
  };

  const handleCloseProjectActionMenu = () => {
    setProjectActionMenuAnchorEl(null);
    setSelectedProjectForAction(null);
  };

  // Handle delete project
  const handleOpenDeleteDialog = (project: string) => {
    setProjectToDelete(project);
    setDeleteProjectDialogOpen(true);
    handleCloseProjectActionMenu();
  };

  const handleCloseDeleteDialog = () => {
    setDeleteProjectDialogOpen(false);
    setProjectToDelete(null);
  };

  const handleDeleteProject = () => {
    if (projectToDelete) {
      // Filter out the project to delete
      const updatedProjects = projects.filter(p => p !== projectToDelete);
      
      // If we're deleting the current project, switch to the first available one
      if (projectToDelete === currentProject && updatedProjects.length > 0) {
        setCurrentProject(updatedProjects[0]);
      } else if (updatedProjects.length === 0) {
        // If no projects left, create a default one
        setProjects(['Default Project']);
        setCurrentProject('Default Project');
      } else {
        setProjects(updatedProjects);
      }
      
      handleCloseDeleteDialog();
    }
  };

  // Department options
  const departments = [
    'Finance',
    'Procurement',
    'IT',
    'Marketing',
    'Operations',
    'Human Resources',
    'Research & Development',
    'Sales'
  ];
  
  // Project type options
  const projectTypes = [
    'Spend Analysis',
    'Supplier Consolidation',
    'Cost Savings',
    'Contract Negotiation',
    'Strategic Sourcing',
    'Vendor Management',
    'Spend Optimization',
    'Procurement Transformation'
  ];
  
  // Currency options
  const currencies = ['USD', 'EUR', 'GBP', 'AUD', 'CAD', 'JPY', 'CNY'];
  
  // Tag suggestions
  const tagSuggestions = [
    'High Priority',
    'Strategic',
    'Cost Saving',
    'Quarterly Review',
    'Annual Budget',
    'IT Procurement',
    'Vendor Management',
    'Direct Spend',
    'Indirect Spend',
    'Services'
  ];
  
  // Handle project field changes
  const handleProjectChange = (field: keyof ProjectData, value: any) => {
    setNewProject(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <FileDataProvider>
        <Box sx={{ display: 'flex', height: '100vh' }}>
          {/* Sidebar */}
          <Drawer
            variant="permanent"
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                borderRight: '1px solid #e0e0e0',
                transition: 'width 0.3s ease',
                overflowX: 'hidden',
              },
            }}
          >
            <Toolbar sx={{ px: 2, py: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: '50%', 
                    background: 'linear-gradient(135deg, #8e2c4c 0%, #6e1c3c 100%)',
                    mr: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    component="img"
                    src="/images/iris-logo.png"
                    alt="IRIS Logo"
                    sx={{ 
                      width: 20, 
                      height: 20
                    }}
                  />
                </Box>
                <Typography 
                  variant="h6" 
                  component="div" 
                  ref={titleRef}
                  sx={{ 
                    whiteSpace: 'nowrap',
                    overflow: 'visible'
                  }}
                >
                  {currentProject} spend
                </Typography>
              </Box>
            </Toolbar>
            <Box sx={{ px: 2, py: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  PROJECT
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={handleOpenNewProjectDialog}
                  sx={{ color: 'primary.main' }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box 
                sx={{ 
                  p: 1, 
                  borderRadius: 1, 
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  maxWidth: '100%',
                  cursor: 'pointer'
                }}
                onClick={handleOpenProjectMenu}
              >
                <Typography variant="body1" sx={{ fontWeight: 500, maxWidth: 'calc(100% - 24px)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {currentProject}
                </Typography>
                <ArrowDropDownIcon fontSize="small" />
              </Box>
            </Box>
            <List sx={{ mt: 2, width: '100%' }}>
              {/* Main menu items */}
              {mainMenuItems.map((item) => (
                <ListItem 
                  button 
                  key={item.text}
                  onClick={() => navigate(item.path)}
                  sx={{ 
                    borderRadius: 1,
                    mx: 1,
                    mb: 0.5,
                    backgroundColor: location.pathname === item.path ? 'rgba(142, 44, 76, 0.08)' : 'transparent',
                    color: location.pathname === item.path ? 'primary.main' : 'text.primary',
                    '&:hover': {
                      backgroundColor: 'rgba(142, 44, 76, 0.08)',
                    },
                    width: 'calc(100% - 16px)',
                    maxWidth: 'calc(100% - 16px)'
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: location.pathname === item.path ? 'primary.main' : 'text.secondary',
                    minWidth: 40
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text} 
                    sx={{ 
                      '& .MuiTypography-root': { 
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      } 
                    }}
                  />
                </ListItem>
              ))}

              {/* Spend Analysis parent menu item */}
              <ListItem 
                button 
                onClick={handleSpendAnalysisClick}
                sx={{ 
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                  backgroundColor: spendAnalysisOpen ? 'rgba(142, 44, 76, 0.08)' : 'transparent',
                  color: spendAnalysisOpen ? 'primary.main' : 'text.primary',
                  '&:hover': {
                    backgroundColor: 'rgba(142, 44, 76, 0.08)',
                  },
                  width: 'calc(100% - 16px)',
                  maxWidth: 'calc(100% - 16px)'
                }}
              >
                <ListItemIcon sx={{ 
                  color: spendAnalysisOpen ? 'primary.main' : 'text.secondary',
                  minWidth: 40
                }}>
                  <BarChartIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Spend Analysis" 
                  sx={{ 
                    '& .MuiTypography-root': { 
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    } 
                  }}
                />
                {spendAnalysisOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItem>

              {/* Spend Analysis submenu */}
              <Collapse in={spendAnalysisOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {spendAnalysisSubMenuItems.map((item) => (
                    <ListItem 
                      button 
                      key={item.text}
                      onClick={() => navigate(item.path)}
                      sx={{ 
                        pl: 4,
                        borderRadius: 1,
                        mx: 1,
                        mb: 0.5,
                        backgroundColor: location.pathname === item.path ? 'rgba(142, 44, 76, 0.08)' : 'transparent',
                        color: location.pathname === item.path ? 'primary.main' : 'text.primary',
                        '&:hover': {
                          backgroundColor: 'rgba(142, 44, 76, 0.08)',
                        },
                        width: 'calc(100% - 16px)',
                        maxWidth: 'calc(100% - 16px)'
                      }}
                    >
                      <ListItemIcon sx={{ 
                        color: location.pathname === item.path ? 'primary.main' : 'text.secondary',
                        minWidth: 40
                      }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.text} 
                        sx={{ 
                          '& .MuiTypography-root': { 
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          } 
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </List>
            <Box sx={{ mt: 'auto', mb: 2 }}>
              <List>
                <ListItem 
                  button 
                  sx={{ 
                    borderRadius: 1, 
                    mx: 1,
                    width: 'calc(100% - 16px)',
                    maxWidth: 'calc(100% - 16px)'
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <SettingsIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Settings" 
                    sx={{ 
                      '& .MuiTypography-root': { 
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      } 
                    }}
                  />
                </ListItem>
                <ListItem 
                  button 
                  sx={{ 
                    borderRadius: 1, 
                    mx: 1,
                    width: 'calc(100% - 16px)',
                    maxWidth: 'calc(100% - 16px)'
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <HelpIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Help" 
                    sx={{ 
                      '& .MuiTypography-root': { 
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      } 
                    }}
                  />
                </ListItem>
              </List>
            </Box>

            {/* Project Menu */}
            <Menu
              anchorEl={projectMenuAnchorEl}
              open={Boolean(projectMenuAnchorEl)}
              onClose={handleCloseProjectMenu}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              {projects.map((project) => (
                <MenuItem 
                  key={project} 
                  onClick={() => handleSelectProject(project)}
                  selected={project === currentProject}
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    pr: 1
                  }}
                >
                  <Typography sx={{ mr: 2 }}>{project}</Typography>
                  <IconButton 
                    size="small" 
                    onClick={(e) => handleOpenProjectActionMenu(e, project)}
                    sx={{ ml: 'auto', visibility: projects.length > 1 ? 'visible' : 'hidden' }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </MenuItem>
              ))}
            </Menu>
            
            {/* Project Action Menu */}
            <Menu
              anchorEl={projectActionMenuAnchorEl}
              open={Boolean(projectActionMenuAnchorEl)}
              onClose={handleCloseProjectActionMenu}
            >
              <MenuItem 
                onClick={() => selectedProjectForAction && handleOpenDeleteDialog(selectedProjectForAction)}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon>
                  <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                </ListItemIcon>
                <ListItemText primary="Delete" />
              </MenuItem>
            </Menu>
            
            {/* Delete Project Confirmation Dialog */}
            <Dialog open={deleteProjectDialogOpen} onClose={handleCloseDeleteDialog}>
              <DialogTitle>Delete Project</DialogTitle>
              <DialogContent>
                <Typography>
                  Are you sure you want to delete the project "{projectToDelete}"? This action cannot be undone.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                <Button 
                  onClick={handleDeleteProject} 
                  variant="contained" 
                  color="error"
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
            
            {/* New Project Dialog - Enhanced */}
            <Dialog 
              open={newProjectDialogOpen} 
              onClose={handleCloseNewProjectDialog}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>Create New Project</DialogTitle>
              <DialogContent sx={{ pb: 2 }}>
                <Grid container spacing={3} sx={{ mt: 0 }}>
                  {/* Project Name */}
                  <Grid item xs={12}>
                    <TextField
                      autoFocus
                      required
                      margin="dense"
                      id="project-name"
                      label="Project Name"
                      type="text"
                      fullWidth
                      variant="outlined"
                      value={newProject.name || ''}
                      onChange={(e) => handleProjectChange('name', e.target.value)}
                      helperText="Enter a unique project name"
                    />
                  </Grid>
                  
                  {/* Project Description */}
                  <Grid item xs={12}>
                    <TextField
                      margin="dense"
                      id="project-description"
                      label="Description"
                      multiline
                      rows={3}
                      fullWidth
                      variant="outlined"
                      value={newProject.description || ''}
                      onChange={(e) => handleProjectChange('description', e.target.value)}
                      helperText="Brief description of the project's purpose"
                    />
                  </Grid>
                  
                  {/* Dates Row */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="dense"
                      id="project-start-date"
                      label="Start Date"
                      type="date"
                      fullWidth
                      variant="outlined"
                      value={newProject.startDate || ''}
                      onChange={(e) => handleProjectChange('startDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="dense"
                      id="project-end-date"
                      label="End Date (Optional)"
                      type="date"
                      fullWidth
                      variant="outlined"
                      value={newProject.endDate || ''}
                      onChange={(e) => handleProjectChange('endDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  
                  {/* Department and Type Row */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="dense">
                      <InputLabel id="department-label">Department</InputLabel>
                      <Select
                        labelId="department-label"
                        id="department"
                        value={newProject.department || ''}
                        onChange={(e) => handleProjectChange('department', e.target.value)}
                        label="Department"
                      >
                        {departments.map((dept) => (
                          <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="dense">
                      <InputLabel id="project-type-label">Project Type</InputLabel>
                      <Select
                        labelId="project-type-label"
                        id="project-type"
                        value={newProject.type || 'Spend Analysis'}
                        onChange={(e) => handleProjectChange('type', e.target.value)}
                        label="Project Type"
                      >
                        {projectTypes.map((type) => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Budget Row */}
                  <Grid item xs={12} sm={8}>
                    <TextField
                      margin="dense"
                      id="project-budget"
                      label="Budget"
                      type="number"
                      fullWidth
                      variant="outlined"
                      value={newProject.budget || 0}
                      onChange={(e) => handleProjectChange('budget', parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth margin="dense">
                      <InputLabel id="currency-label">Currency</InputLabel>
                      <Select
                        labelId="currency-label"
                        id="currency"
                        value={newProject.currency || 'USD'}
                        onChange={(e) => handleProjectChange('currency', e.target.value)}
                        label="Currency"
                      >
                        {currencies.map((currency) => (
                          <MenuItem key={currency} value={currency}>{currency}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Tags */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      id="project-tags"
                      options={tagSuggestions}
                      freeSolo
                      value={newProject.tags || []}
                      onChange={(_, newValue) => handleProjectChange('tags', newValue)}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip 
                            label={option} 
                            {...getTagProps({ index })} 
                            sx={{ 
                              backgroundColor: 'rgba(142, 44, 76, 0.1)',
                              '& .MuiChip-deleteIcon': {
                                color: 'primary.main'
                              }
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Tags"
                          helperText="Add tags to organize your projects (press Enter after each tag)"
                          margin="dense"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={handleCloseNewProjectDialog} variant="outlined">Cancel</Button>
                <Button 
                  onClick={handleCreateNewProject} 
                  variant="contained" 
                  color="primary"
                  disabled={!newProject.name || newProject.name.trim() === ''}
                >
                  Create Project
                </Button>
              </DialogActions>
            </Dialog>
          </Drawer>

          {/* Main content */}
          <Box component="main" sx={{ flexGrow: 1, p: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <AppBar position="static" elevation={0}>
              <Toolbar>
                <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                  {pageTitle}
                </Typography>
              </Toolbar>
            </AppBar>
            <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
              <Routes>
                <Route path="/" element={<Navigate to="/upload" replace />} />
                <Route path="/dashboard" element={<Navigate to="/upload" replace />} />
                <Route path="/upload" element={<FileUploadPage />} />
                <Route path="/processing" element={<ProcessingPage />} />
                <Route path="/analysis" element={<DataAnalysisPage />} />
                <Route path="/chat" element={<ChatPage />} />
                {/* New Dashboard Routes */}
                <Route path="/dashboard/data-prep" element={<DataPrepPage />} />
                <Route path="/dashboard/spend-cube" element={<SpendCubeDimensionsPage />} />
                <Route path="/dashboard/supplier-consolidation" element={<SupplierConsolidationPage />} />
                <Route path="/dashboard/price-variances" element={<PriceVariancesPage />} />
                <Route path="/dashboard/tail-spend" element={<TailSpendPage />} />
                <Route path="/dashboard/process-efficiency" element={<ProcessEfficiencyPage />} />
                <Route path="/dashboard/sustainability" element={<SustainabilityPage />} />
                <Route path="/dashboard/opportunity-roadmap" element={<OpportunityRoadmapPage />} />
              </Routes>
            </Box>
            
            {/* Powered by IRIS label */}
            <Box 
              sx={{ 
                position: 'fixed', 
                bottom: 16, 
                right: 16, 
                display: 'flex', 
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(4px)',
                padding: '6px 12px',
                borderRadius: 16,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                zIndex: 1000
              }}
            >
              <Box 
                component="img"
                src="/images/iris-logo.png"
                alt="IRIS Logo"
                sx={{ 
                  width: 20, 
                  height: 20, 
                  mr: 1
                }}
              />
              <Typography 
                variant="caption" 
                sx={{ 
                  fontWeight: 600, 
                  letterSpacing: 0.5,
                  color: '#333'
                }}
              >
                POWERED BY IRIS
              </Typography>
            </Box>
          </Box>
        </Box>
      </FileDataProvider>
    </ThemeProvider>
  );
}

export default App; 